import type { AttachmentKind } from '@/api/attachments'
import type {
  ChatContextSummary,
  ChatMessageRead,
  EditProposalRead,
  Field,
  FieldKind,
  FieldProposalEvent,
} from '@/api/docfiller-types'
import { isCheckboxChecked } from '@/api/docfiller-types'

/**
 * Minimal sent-attachment payload rendered inline in the transcript so the
 * user can see what context went out with the prompt. We deliberately do
 * NOT carry the upload status or the raw `r2_key` — by the time we mint
 * one of these the attachment is ready, sent, and immutable.
 */
export interface SentAttachmentSummary {
  uuid: string
  filename: string
  kind: AttachmentKind
  byteSize: number
}

/**
 * Normalised, human-facing view of a single field_proposal SSE event.
 * The Chat panel resolves the friendly name + kind from `doc.fields` at
 * the SSE boundary so downstream rendering never has to think about raw
 * XFA / AcroForm IDs again.
 */
export interface NormalizedProposal {
  /** Raw XFA / AcroForm name from the backend (kept for debug + tooltip). */
  rawFieldName: string
  /** Human-friendly label, or `rawFieldName` when the field has none. */
  displayName: string
  /** True when we found a friendly name and `displayName !== rawFieldName`. */
  hasFriendlyName: boolean
  /** Raw stored value as sent on the wire. */
  value: string
  /** Humanised value ready to render in default UI. */
  displayValue: string
  /** `true` when the proposal cleared / blanked the field. */
  isEmpty: boolean
  /** Field's typed kind, resolved from `doc.fields` if available. */
  fieldKind: FieldKind | null
  confidence: number | null
  /** Rationale text, only when it adds real information vs. the value. */
  reasoning: string | null
  fieldUuid: string | null
  runId: string
  applied: boolean
}

/**
 * One rendered row in the chat transcript. Sourced from a mix of local
 * (user-typed) entries, SSE message / field_proposal / error events, and
 * synthetic status markers (run start / done / stopped).
 *
 * `proposal` carries a pre-normalised payload — never the raw SSE event —
 * so the default UI cannot accidentally leak raw XFA names or booleans.
 *
 * `at` is the wall-clock time the entry landed on the client (or, for
 * persisted-transcript entries, the row's `created_at`). Optional so the
 * test fixtures and the live-stream path can keep working without
 * threading a timestamp through every append.
 */
export type ChatEntry =
  | { kind: 'user'; content: string; at?: Date }
  | { kind: 'assistant'; content: string; at?: Date }
  | {
      kind: 'tool'
      name: string | null
      summary: string | null
      parsed: Record<string, unknown>
      at?: Date
    }
  | { kind: 'proposal'; proposal: NormalizedProposal; at?: Date }
  | {
      kind: 'attachments'
      attachments: readonly SentAttachmentSummary[]
      at?: Date
    }
  | { kind: 'run-start'; runId: string; at?: Date }
  | { kind: 'run-done'; at?: Date }
  | { kind: 'run-stopped'; at?: Date }
  | {
      kind: 'error'
      content: string
      at?: Date
      /**
       * When set, the failed send can be retried verbatim by re-invoking
       * the panel's send handler with this prompt + attachment list. Only
       * the most recent retryable error renders a button — see
       * `lastRetryableErrorIndex` in `Chat.tsx`.
       */
      retryable?: {
        prompt: string
        attachments: readonly SentAttachmentSummary[]
      }
    }
  | { kind: 'pending-proposal'; proposal: EditProposalRead; at?: Date }
  | { kind: 'context-used'; summary: ChatContextSummary; at?: Date }

const TOOL_NAME_KEYS = ['tool', 'tool_name', 'name', 'function', 'action'] as const
const TOOL_SUMMARY_KEYS = [
  'query',
  'value',
  'message',
  'input',
  'reason',
  'reasoning',
  'description',
  'args',
] as const

/**
 * Best-effort detection of "tool call" JSON the agent leaks as raw text into
 * the chat stream. Returns `null` for plain prose; otherwise returns a
 * parsed payload plus a name/summary picked from common shapes.
 */
export function tryParseToolCall(content: string): {
  name: string | null
  summary: string | null
  parsed: Record<string, unknown>
} | null {
  const trimmed = content.trim()
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null

  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch {
    return null
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null

  const obj = parsed as Record<string, unknown>
  return {
    name: pickString(obj, TOOL_NAME_KEYS),
    summary: pickSummary(obj),
    parsed: obj,
  }
}

const FIELD_SUGGESTION_SUMMARY_RE = /^Suggested\s+\d+\s+fields?\.?$/i

/**
 * Strip raw field-suggestion NDJSON from assistant prose before it reaches
 * the visible transcript. The structured `field_proposal` events still carry
 * value/confidence/reasoning for the polished cards; this only removes a
 * duplicate provider text dump such as:
 *
 *   {"field_name":"...","value":"...","confidence":0.9,"reasoning":"..."}
 *   Suggested 24 fields
 */
export function sanitizeAssistantText(content: string): string | null {
  const withoutDebugJson = stripFieldSuggestionJsonBlocks(content)
  const trimmed = withoutDebugJson.trim()
  if (!trimmed) return null

  const kept: string[] = []
  for (const rawLine of trimmed.split(/\r?\n/)) {
    const line = rawLine.trim()
    if (!line) continue
    if (looksLikeFieldSuggestionLine(line)) {
      continue
    }
    if (FIELD_SUGGESTION_SUMMARY_RE.test(line)) {
      continue
    }
    kept.push(rawLine.replace(/\s+$/, ''))
  }

  const cleaned = kept.join('\n').trim()
  return cleaned.length > 0 ? cleaned : null
}

function looksLikeFieldSuggestionLine(line: string): boolean {
  if (!line.startsWith('{') || !line.endsWith('}')) return false
  let parsed: unknown
  try {
    parsed = JSON.parse(line)
  } catch {
    return false
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false
  const obj = parsed as Record<string, unknown>
  const fieldName = obj.field_name ?? obj.name
  return typeof fieldName === 'string' && typeof obj.value === 'string'
}

function stripFieldSuggestionJsonBlocks(content: string): string {
  let out = ''
  let cursor = 0
  while (cursor < content.length) {
    const start = content.indexOf('{', cursor)
    if (start === -1) {
      out += content.slice(cursor)
      break
    }
    const end = findJsonObjectEnd(content, start)
    if (end === -1) {
      out += content.slice(cursor)
      break
    }
    const candidate = content.slice(start, end + 1)
    if (looksLikeFieldSuggestionJson(candidate)) {
      out += content.slice(cursor, start)
      cursor = end + 1
      continue
    }
    out += content.slice(cursor, end + 1)
    cursor = end + 1
  }
  return out
}

function findJsonObjectEnd(content: string, start: number): number {
  let depth = 0
  let inString = false
  let escaped = false
  for (let i = start; i < content.length; i += 1) {
    const ch = content[i]
    if (inString) {
      if (escaped) {
        escaped = false
      } else if (ch === '\\') {
        escaped = true
      } else if (ch === '"') {
        inString = false
      }
      continue
    }
    if (ch === '"') {
      inString = true
      continue
    }
    if (ch === '{') {
      depth += 1
    } else if (ch === '}') {
      depth -= 1
      if (depth === 0) return i
      if (depth < 0) return -1
    }
  }
  return -1
}

function looksLikeFieldSuggestionJson(content: string): boolean {
  let parsed: unknown
  try {
    parsed = JSON.parse(content)
  } catch {
    return false
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false
  const obj = parsed as Record<string, unknown>
  const fieldName = obj.field_name ?? obj.name
  return (
    typeof fieldName === 'string' &&
    'value' in obj &&
    ('confidence' in obj || 'reasoning' in obj || 'field_uuid' in obj)
  )
}

function pickString(
  obj: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    const v = obj[key]
    if (typeof v === 'string' && v.trim().length > 0) return v
  }
  return null
}

function pickSummary(obj: Record<string, unknown>): string | null {
  for (const key of TOOL_SUMMARY_KEYS) {
    const v = obj[key]
    if (typeof v === 'string' && v.trim().length > 0) {
      return truncate(v, 80)
    }
    if (v && typeof v === 'object') {
      const inner = pickString(v as Record<string, unknown>, TOOL_SUMMARY_KEYS)
      if (inner) return truncate(inner, 80)
    }
  }
  return null
}

function truncate(s: string, max: number): string {
  const flat = s.replace(/\s+/g, ' ').trim()
  return flat.length <= max ? flat : `${flat.slice(0, max - 1)}…`
}

/**
 * Format an agent confidence (0..1) as a rounded percentage, or `null` if the
 * upstream value is missing/garbage.
 */
export function formatConfidence(confidence: number | null): string | null {
  if (typeof confidence !== 'number' || !Number.isFinite(confidence)) return null
  return `${Math.round(confidence * 100)}%`
}

/**
 * Heuristic: does this look like a raw XFA / AcroForm field id rather than a
 * human label? We use this only as a fallback when `doc.fields` doesn't have
 * a friendly_name — to decide whether the raw name is safe to show inline.
 */
export function looksLikeRawFieldName(name: string): boolean {
  if (!name) return false
  // XFA paths and AcroForm internal ids use brackets, dots, and ALL CAPS
  // tokens like TOPMOSTSUBFORM[0], topmostSubform[0].Page1[0]...
  if (/\[\d+\]/.test(name)) return true
  if (/[.][A-Za-z_][A-Za-z0-9_]*\[/.test(name)) return true
  // Long all-caps tokens (TOPMOSTSUBFORM, PDFTK, etc.)
  if (/[A-Z]{6,}/.test(name) && !name.includes(' ')) return true
  // Single-token snake_case or camelCase looks like a code identifier.
  if (!name.includes(' ') && /[a-z][A-Z]/.test(name)) return true
  return false
}

/**
 * Map a stored field value into something a non-developer can read. The
 * backend canonicalises checkbox values to "true"/"false" but legacy rows
 * (and tests) sometimes carry "/Yes", "yes", "off", etc. Empty values
 * become a soft placeholder so the run doesn't read as "filled `` (empty)`".
 */
export function humanizeValue(
  value: string,
  kind: FieldKind | string | null,
): { displayValue: string; isEmpty: boolean } {
  const trimmed = value.trim()
  if (kind === 'checkbox') {
    const checked = isCheckboxChecked(trimmed || null)
    return { displayValue: checked ? 'Checked' : 'Unchecked', isEmpty: false }
  }
  if (kind === 'signature') {
    if (trimmed.length === 0) return { displayValue: 'Left blank', isEmpty: true }
    return { displayValue: `Signed as ${trimmed}`, isEmpty: false }
  }
  if (trimmed.length === 0) {
    return { displayValue: 'Left blank', isEmpty: true }
  }
  return { displayValue: trimmed, isEmpty: false }
}

/**
 * Decide whether the agent's rationale string carries information beyond
 * "the value of this field is X". We hide reasoning that just restates the
 * field name + value, or that's empty, so the chat doesn't drown in
 * boilerplate Why? affordances.
 */
export function meaningfulReasoning(
  reasoning: string | null,
  proposal: { value: string; rawFieldName: string; displayName: string },
): string | null {
  if (!reasoning) return null
  const trimmed = reasoning.trim()
  if (trimmed.length === 0) return null
  // 3 words or fewer that mostly repeat the value/name is noise.
  const flat = trimmed.toLowerCase()
  const value = proposal.value.trim().toLowerCase()
  const display = proposal.displayName.trim().toLowerCase()
  const raw = proposal.rawFieldName.trim().toLowerCase()
  if (flat === value) return null
  if (flat === display) return null
  if (flat === raw) return null
  if (flat.length < 8) return null
  return trimmed
}

interface FieldRef {
  uuid: string
  name: string
  friendly_name?: string | null
  kind: FieldKind | string
}

/**
 * Turn a raw SSE field_proposal event into a `NormalizedProposal` ready for
 * the chat to render. Resolves friendly_name + kind from `doc.fields`,
 * humanises the value, and trims reasoning noise.
 */
export function normalizeProposal(
  event: FieldProposalEvent,
  fields: readonly FieldRef[],
): NormalizedProposal {
  const match = findField(event, fields)
  const friendly = event.friendly_name?.trim() || match?.friendly_name?.trim()
  const hasFriendlyName = !!friendly && friendly.length > 0
  let displayName: string
  if (hasFriendlyName) {
    displayName = friendly
  } else if (match && !looksLikeRawFieldName(match.name)) {
    displayName = match.name
  } else if (!looksLikeRawFieldName(event.field_name)) {
    displayName = event.field_name
  } else {
    // We have neither friendly_name nor a presentable raw name. Best we can
    // do is fall back to the raw id — but flag it as not-friendly so the
    // bubble can render it as a code-styled hint instead of a label.
    displayName = match?.name ?? event.field_name
  }

  const fieldKind =
    (event.field_kind as FieldKind | undefined) ??
    (match?.kind as FieldKind | undefined) ??
    null
  const { displayValue, isEmpty } = humanizeValue(event.value, fieldKind)
  const reasoning = meaningfulReasoning(event.reasoning, {
    value: event.value,
    rawFieldName: event.field_name,
    displayName,
  })

  return {
    rawFieldName: event.field_name,
    displayName,
    hasFriendlyName,
    value: event.value,
    displayValue,
    isEmpty,
    fieldKind,
    confidence: event.confidence,
    reasoning,
    fieldUuid: event.field_uuid,
    runId: event.run_id,
    applied: event.applied,
  }
}

function findField(
  event: FieldProposalEvent,
  fields: readonly FieldRef[],
): FieldRef | null {
  if (event.field_uuid) {
    const byUuid = fields.find((f) => f.uuid === event.field_uuid)
    if (byUuid) return byUuid
  }
  return fields.find((f) => f.name === event.field_name) ?? null
}

// Convenience adapter for callers that hand us the full `Field` shape from
// the document detail response.
export function normalizeProposalFromDoc(
  event: FieldProposalEvent,
  fields: readonly Field[],
): NormalizedProposal {
  return normalizeProposal(event, fields)
}

/**
 * Format a short run id (first 8 chars) for the debug-only run tag. The
 * default UI never surfaces this — only the expanded debug disclosure.
 */
export function shortRunId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id
}

/**
 * Append `entry` to the chat log, merging it with the previous entry when
 * both are plain assistant text. The backend splits agent output on `\n`,
 * so a single logical reply can otherwise arrive as 3-4 sibling bubbles.
 *
 * Also collapses redundant run-start / run-done / run-stopped markers so
 * an aborted run that's restarted in the same session doesn't leave a
 * trail of duplicate status chips.
 */
export function appendChatEntry(
  prev: readonly ChatEntry[],
  entry: ChatEntry,
): ChatEntry[] {
  if (entry.kind === 'assistant' && prev.length > 0) {
    const last = prev[prev.length - 1]
    if (last && last.kind === 'assistant') {
      return [
        ...prev.slice(0, -1),
        { kind: 'assistant', content: `${last.content}\n${entry.content}`, at: last.at },
      ]
    }
  }
  if (
    entry.kind === 'run-start' ||
    entry.kind === 'run-done' ||
    entry.kind === 'run-stopped'
  ) {
    const last = prev[prev.length - 1]
    if (last && last.kind === entry.kind) {
      return [...prev]
    }
  }
  return [...prev, entry]
}

/**
 * Count proposals that belong to a given run id within `entries`. Used to
 * render the run-done summary chip ("Filled 7 fields") without re-emitting
 * a status entry per proposal.
 */
export function countProposalsForRun(
  entries: readonly ChatEntry[],
  runId: string,
): { filled: number; suggested: number } {
  let filled = 0
  let suggested = 0
  for (const e of entries) {
    if (e.kind !== 'proposal') continue
    if (e.proposal.runId !== runId) continue
    if (e.proposal.applied) filled += 1
    else suggested += 1
  }
  return { filled, suggested }
}

const TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
})
const DAY_TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  hour: 'numeric',
  minute: '2-digit',
})
const FULL_FORMAT = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

/**
 * Compact, human-friendly timestamp for a chat bubble. Same-day events
 * collapse to "1:23 PM"; events within the last week add a weekday
 * prefix; older events fall back to a medium date+time. Returns `null`
 * when `at` is null/undefined or unparseable so callers can skip the
 * `<time>` element rather than render an empty placeholder.
 *
 * `now` is exposed so tests can pin the reference clock without faking
 * `Date.now`.
 */
export function formatChatTimestamp(
  at: Date | string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (at == null) return null
  const d = at instanceof Date ? at : new Date(at)
  if (Number.isNaN(d.getTime())) return null
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  if (sameDay) return TIME_FORMAT.format(d)
  const daysAgo = Math.floor(
    (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24),
  )
  if (daysAgo >= 0 && daysAgo < 7) return DAY_TIME_FORMAT.format(d)
  return FULL_FORMAT.format(d)
}

/**
 * Long-form timestamp used in tooltips and `title` attributes. Mirrors
 * the format used by the audit log and chat history list so the same
 * tooltip text appears across the app.
 */
export function fullChatTimestamp(
  at: Date | string | null | undefined,
): string | null {
  if (at == null) return null
  const d = at instanceof Date ? at : new Date(at)
  if (Number.isNaN(d.getTime())) return null
  return FULL_FORMAT.format(d)
}

/**
 * Citation chip surfaced under a proposal or assistant message — calls
 * out the document field, attachment, or other source the agent
 * referenced when producing its value. The Chat panel renders these as
 * pill-shaped chips below the bubble.
 *
 *   `kind: 'field'`      — `label` is the friendly field name, `detail`
 *                          can carry the page number for multi-page docs
 *   `kind: 'attachment'` — `label` is the filename, `detail` is the
 *                          attachment kind ("screenshot", "pdf", …)
 *   `kind: 'context'`    — generic source ("workspace context", "user
 *                          profile") for explanations that don't map onto
 *                          a structured row
 */
export type CitationSource =
  | { kind: 'field'; label: string; detail?: string | null }
  | { kind: 'attachment'; label: string; detail?: string | null }
  | { kind: 'context'; label: string; detail?: string | null }

/**
 * Pull citation chips out of an entry. For a proposal we expose the
 * source field (so the bubble visibly attributes the value to the field
 * it came from); we leave attachment citations to callers because the
 * SSE field_proposal event doesn't carry per-proposal attachment refs.
 */
export function proposalCitations(
  proposal: NormalizedProposal,
): CitationSource[] {
  if (!proposal.hasFriendlyName) return []
  return [{ kind: 'field', label: proposal.displayName }]
}

const TRANSCRIPT_SOURCE_KEYS = ['sources', 'citations'] as const

/**
 * Pull citation chips off a persisted `ChatMessageRead.data` payload.
 * Tolerates a small set of shapes the backend may emit now or in the
 * future without forcing the frontend to chase a typed migration:
 *
 *   - `data.sources` or `data.citations` as an array of strings
 *   - `data.sources` as an array of `{kind, label, detail?}` rows
 *   - `data.field_name` (string) — single field citation
 *   - `data.context_files` (array of `{filename, kind}`) — attachment
 *     citations carried alongside a stored proposal/text message
 */
export function transcriptCitations(
  data: Record<string, unknown> | null | undefined,
): CitationSource[] {
  if (!data) return []
  const out: CitationSource[] = []
  const fieldName = data.field_name
  if (typeof fieldName === 'string' && fieldName.trim().length > 0) {
    out.push({ kind: 'field', label: fieldName.trim() })
  }
  for (const key of TRANSCRIPT_SOURCE_KEYS) {
    const raw = data[key]
    if (!Array.isArray(raw)) continue
    for (const row of raw) {
      if (typeof row === 'string' && row.trim().length > 0) {
        out.push({ kind: 'context', label: row.trim() })
        continue
      }
      if (row && typeof row === 'object') {
        const obj = row as Record<string, unknown>
        const label = typeof obj.label === 'string' ? obj.label.trim() : null
        if (!label) continue
        const kindRaw = obj.kind
        const kind =
          kindRaw === 'field' || kindRaw === 'attachment' || kindRaw === 'context'
            ? kindRaw
            : 'context'
        const detail =
          typeof obj.detail === 'string' && obj.detail.trim().length > 0
            ? obj.detail.trim()
            : null
        out.push({ kind, label, detail })
      }
    }
  }
  const ctx = data.context_files
  if (Array.isArray(ctx)) {
    for (const row of ctx) {
      if (!row || typeof row !== 'object') continue
      const obj = row as Record<string, unknown>
      const filename =
        typeof obj.filename === 'string' ? obj.filename.trim() : null
      if (!filename) continue
      const detail =
        typeof obj.kind === 'string' && obj.kind.trim().length > 0
          ? obj.kind.trim()
          : null
      out.push({ kind: 'attachment', label: filename, detail })
    }
  }
  return out
}

interface ChatMessageDataRef {
  applied?: unknown
  confidence?: unknown
  field_name?: unknown
  friendly_name?: unknown
  field_kind?: unknown
  field_uuid?: unknown
  value?: unknown
  reasoning?: unknown
  run_id?: unknown
}

function looksLikeJsonObject(content: string): boolean {
  const trimmed = content.trim()
  if (trimmed.length < 2) return false
  return (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  )
}

/**
 * Replay a persisted chat session (from `GET /dashboard/chats/{uuid}`)
 * as a list of `ChatEntry` rows the conversation pane can render with
 * the same bubble components as a live stream. Best-effort: rows we
 * can't classify fall through as plain text bubbles rather than being
 * dropped, so the user always sees *something* and the transcript stays
 * useful even if the persisted schema gains new fields.
 */
export function chatMessagesToEntries(
  messages: readonly ChatMessageRead[],
  fields: readonly FieldRef[],
): ChatEntry[] {
  const entries: ChatEntry[] = []
  for (const m of messages) {
    const at = m.created_at ? new Date(m.created_at) : undefined
    if (m.kind === 'field_proposal') {
      const data = (m.data ?? {}) as ChatMessageDataRef
      const fakeEvent: FieldProposalEvent = {
        field_name: typeof data.field_name === 'string' ? data.field_name : '',
        friendly_name:
          typeof data.friendly_name === 'string' ? data.friendly_name : null,
        field_kind: typeof data.field_kind === 'string' ? data.field_kind : null,
        value: typeof data.value === 'string' ? data.value : m.content,
        confidence:
          typeof data.confidence === 'number' ? data.confidence : null,
        reasoning:
          typeof data.reasoning === 'string' ? data.reasoning : null,
        field_uuid:
          typeof data.field_uuid === 'string' ? data.field_uuid : null,
        run_id: typeof data.run_id === 'string' ? data.run_id : '',
        applied: data.applied === true,
      }
      const proposal = normalizeProposal(fakeEvent, fields)
      entries.push({ kind: 'proposal', proposal, at })
      continue
    }
    if (m.kind === 'status' || m.role === 'system') {
      // Statuses we recognise collapse to run-start/run-done chips; the
      // rest fall through as a generic error chip so they're visible.
      const content = m.content.trim()
      if (/^run[\s_-]?start\b/i.test(content)) {
        const runId =
          typeof (m.data as ChatMessageDataRef | null)?.run_id === 'string'
            ? ((m.data as ChatMessageDataRef).run_id as string)
            : ''
        entries.push({ kind: 'run-start', runId, at })
        continue
      }
      if (/^(run[\s_-]?done|done|finished)\b/i.test(content)) {
        entries.push({ kind: 'run-done', at })
        continue
      }
      if (/^(run[\s_-]?stopped|stopped|aborted)\b/i.test(content)) {
        entries.push({ kind: 'run-stopped', at })
        continue
      }
      if (/^error\b|^failed\b/i.test(content) || looksLikeError(m)) {
        entries.push({ kind: 'error', content, at })
        continue
      }
      // Unknown status — surface as a neutral error chip rather than
      // silently dropping it; helps debug new server emissions.
      entries.push({ kind: 'error', content, at })
      continue
    }
    if (m.role === 'user') {
      // Strip any embedded tool-call JSON so historical user messages
      // (which never contain tool calls anyway) stay clean text.
      entries.push({ kind: 'user', content: m.content, at })
      continue
    }
    const content = sanitizeAssistantText(m.content)
    if (content == null) continue
    // Assistant text. If it's a tool-call JSON blob (older transcripts),
    // render as a tool-call chip; otherwise as an assistant bubble.
    if (looksLikeJsonObject(content)) {
      const tool = tryParseToolCall(content)
      if (tool) {
        entries.push({ kind: 'tool', ...tool, at })
        continue
      }
    }
    entries.push({ kind: 'assistant', content, at })
  }
  return entries
}

function looksLikeError(m: ChatMessageRead): boolean {
  const data = m.data
  if (!data || typeof data !== 'object') return false
  return 'error' in data
}
